import { defineAsyncComponent, Component, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router/composables';

import { BackdropProfile, ImageFormat, PopularityRankTypes, StreamingChartObjectType } from '@/@types/graphql-types';
import {
	GetGuideModulesDocument,
	GetGuideModulesQuery,
	GetGuideModulesQueryVariables,
} from '@/features/editorial/graphql/queries/GetGuideModules.query';

import { useQuery } from '@/helpers/composables/useApollo';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

const FullLayout = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "full-layout" */ '@/features/editorial/components/home/modules/layouts/FullLayout.vue'
		)
) as Component;
const HalfLayout = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "half-layout" */ '@/features/editorial/components/home/modules/layouts/HalfLayout.vue'
		)
) as Component;
const SidebarLayout = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "sidebar-layout" */ '@/features/editorial/components/home/modules/layouts/SidebarLayout.vue'
		)
) as Component;

const ArticleList = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "article-list" */ '@/features/editorial/components/home/modules/blocks/ArticleList.vue'
		)
) as Component;
const ArticleList2 = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "article-list-2" */ '@/features/editorial/components/home/modules/blocks/ArticleList2.vue'
		)
) as Component;
const ArticleHero = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "article-hero" */ '@/features/editorial/components/home/modules/blocks/ArticleHero.vue'
		)
) as Component;
const ArticleGrid = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "article-grid" */ '@/features/editorial/components/home/modules/blocks/ArticleGrid.vue'
		)
) as Component;
const StreamingCharts = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "streaming-charts" */ '@/features/editorial/components/home/modules/blocks/StreamingCharts.vue'
		)
) as Component;
const YouTubeShorts = defineAsyncComponent(
	() =>
		import(
			/* webpackChunkName: "youtube-shorts" */ '@/features/editorial/components/home/modules/blocks/YouTubeShorts.vue'
		)
) as Component;

const layoutComponents: Record<string, Component> = {
	ROW_FULL: FullLayout,
	ROW_HALF_SPLIT: HalfLayout,
	ROW_SIDEBAR_RIGHT: SidebarLayout,
};

const blockComponents: Record<string, Component> = {
	LATEST_ARTICLES: ArticleList,
	LATEST_FEATURE_ARTICLES: ArticleList,
	ARTICLE_TIMELINE: ArticleList,
	TRENDING_ARTICLES: ArticleList,
	CUSTOM_ARTICLE_1: ArticleHero,
	CUSTOM_ARTICLES_4: ArticleGrid,
	W2W_ARTICLES: ArticleList2,
	STREAMING_CHARTS: StreamingCharts,
	YOUTUBE_SHORTS: YouTubeShorts,
};

export function useGuideModules() {
	const route = useRoute();

	const { language, country } = storeToRefs(useLanguageStore());
	const { activeDomain } = storeToRefs(useRoutingStore());

	const queryVariables = computed<GetGuideModulesQueryVariables>(() => {
		return {
			modules: null,
			fullPath: route.path,
			site: activeDomain.value,
			country: country.value,
			language: language.value,
			streamingChartsFilter: {
				category: PopularityRankTypes.WeeklyPopularitySameContentType,
				objectType: StreamingChartObjectType.Movie,
			},
			profile: BackdropProfile.S1440,
			format: ImageFormat.Webp,
			first: 0,
		};
	});

	const enabled = computed(() => route.name === 'app.editorial.subdomain.home');

	const { result, loading, onError } = useQuery<GetGuideModulesQuery>(GetGuideModulesDocument, queryVariables, {
		errorPolicy: 'all',
		enabled,
	});

	onError(error => {
		captureMessageForSentry(
			'[GraphQL GetGuideModules]:',
			{ error: error, where: 'Component: useGuideModules.ts' },
			'error'
		);
	});

	const modules = computed(() => {
		const node = result.value?.urlV2.node;
		// Narrow down urlV2.node union to HomePage field types (where modules is accessible)
		if (node?.__typename !== 'HomePage') return undefined;
		return node.modules;
	});

	function getLayoutComponent(layout: keyof typeof layoutComponents): Component {
		return layoutComponents[layout];
	}

	function getBlockComponent(block: keyof typeof blockComponents): Component {
		return blockComponents[block];
	}

	return {
		modules,
		loading,
		getLayoutComponent,
		getBlockComponent,
	};
}
